import {
  WORKFLOW_MESSAGE_TYPE_EMAIL,
  WORKFLOW_MESSAGE_TYPE_SMS
} from 'Api/beAutomationApi'
import {
  OPT_WORKFLOWS_LIST_TYPE_WELCOME_SERIES__VALUE,
  //
  WORKFLOW_ACTIVITY_ACTION_CLICKED,
  WORKFLOW_ACTIVITY_ACTION_NOT_CLICKED,
  WORKFLOW_START_TYPE_ACTIVITY,
  //
  WORKFLOW_START_TYPE_SUBSCRIBER_FIELD
} from 'Constants'
import { STORE_KEY_LIST } from 'Constants/'
import getIntl from 'Helpers/intl'
import { logAppError } from 'Helpers/logger'
import { isNullOrEmpty } from 'Helpers/strings'
import { modalEmailMessagesState } from 'Hooks/useModalEmailMessages/useModalEmailMessages'
import { modalSmsMessagesState } from 'Hooks/useModalSmsMessages/useModalSmsMessages'
import { fromUTCtoZoneFormatted, storeGet } from 'mailup-react-helpers'
import {
  addGroup,
  addNewTag,
  copyMessage,
  copySmsMessage,
  createWorkflow,
  fetchEmailEntitiesThatUseMessage,
  fetchEmailStats,
  fetchFilters,
  fetchMessageLinks,
  fetchSmsEntitiesThatUseMessage,
  fetchSmsStats,
  getConditionsDescription,
  getFilters,
  getGroupsDetails,
  getStatusOfWelcomeMessage,
  getStep2Defaults,
  getWorkflowDetails,
  getWorkflowsList,
  loadGroups,
  setEnableWorkflow,
  setupStep2,
  setupStep3,
  updateWorkflow
} from 'Pages/Workflow/recoil/actions/actions'
import {
  emptyWorkflow,
  segmentationCriteriaState,
  workflowCommonDataState,
  workflowDetails,
  WORKFLOWS_START_TYPE
} from 'Pages/Workflow/recoil/state/data'
import {
  modalEditDisableState,
  modalExistWelcomeSeriesWorkflow,
  modalInvalidFirstStepState,
  modalUsedMessageState,
  modalWelcomeMessageNotEnabled
} from 'Pages/Workflow/recoil/state/modals'
import { pageState, wizardSteps } from 'Pages/Workflow/recoil/state/page'
import {
  workflowEmailStatsState,
  workflowSmsStatsState
} from 'Pages/Workflow/recoil/state/stats'

import { OPT_WORKFLOWS_LIST_TYPE_ABANDONED_CART__VALUE } from 'Constants/index'
import { modalActivateSimulateState } from 'Pages/Workflow/recoil/state/modalActivateSimulate'
import {
  activiyTypeExistingFilterState,
  fieldTypeExistingFilterState,
  step2State
} from '../state/step2'
import { invalidFirstStepState, step3State } from '../state/step3'
import { step4State } from '../state/step4'

export const initPageCallback =
  ({ set, snapshot, ...cbi }) =>
  async (idWorkflow, isWelcomeSeriesPreselected = false, wt = undefined) => {
    set(pageState, (prev) => ({
      ...prev,
      loading: true,
      loaded: false,
      isEditingExistingWorkflow: idWorkflow > 0
    }))

    if (idWorkflow <= 0) {
      set(workflowDetails, (prev) => ({
        ...prev,
        ...emptyWorkflow,
        Draft: true
      }))
    }

    if (isWelcomeSeriesPreselected) {
      set(workflowDetails, (prev) => ({
        ...prev,
        Type: OPT_WORKFLOWS_LIST_TYPE_WELCOME_SERIES__VALUE
      }))
    }

    try {
      const [{ isPecPlus, ...details }, { WelcomeMsgEnabled }] =
        await Promise.all([
          getWorkflowDetails(idWorkflow),
          getStatusOfWelcomeMessage()
        ])

      set(workflowDetails, (prev) => ({
        ...prev,
        ...details,
        Type: details.Type ? details.Type : prev.Type
      }))

      set(wizardSteps, (prev) => ({
        ...prev,
        wizardStep: details.WizardStep > 1 ? details.WizardStep : 1 // prev.wizardStep
      }))

      set(pageState, (prev) => ({ ...prev, isPecPlus, WelcomeMsgEnabled }))
    } catch (error) {
      logAppError({
        Component: 'Workflow',
        Method: 'initPageCallback',
        appError: error
      })
      set(pageState, (prev) => ({
        ...prev,
        error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
        severity: 'danger'
      }))
    }

    if (!isNullOrEmpty(wt)) {
      await step1ToStep2Callback({ set, snapshot, ...cbi })(wt)
    }

    set(pageState, (prev) => ({
      ...prev,
      loading: false,
      loaded: true
    }))
  }

export const step1ToStep2Callback =
  ({ set, snapshot }) =>
  async (selectedType) => {
    const nextStep = 2

    const { Id, Type: prevType } = await snapshot.getPromise(workflowDetails)
    const { WelcomeMsgEnabled } = await snapshot.getPromise(pageState)

    set(pageState, (prev) => ({
      ...prev,
      loading: true
    }))

    try {
      if (selectedType === OPT_WORKFLOWS_LIST_TYPE_WELCOME_SERIES__VALUE) {
        const params = {}
        const bodyParams = { Type: 'WelcomeSeries' }
        const { Items: WorkflowsListResult } = await getWorkflowsList(
          params,
          bodyParams
        )

        if (WorkflowsListResult.length > 0) {
          set(modalExistWelcomeSeriesWorkflow, (prev) => ({
            ...prev,
            show: true,
            id: WorkflowsListResult[0].Id,
            name: WorkflowsListResult[0].Name
          }))
        } else {
          if (!WelcomeMsgEnabled) {
            set(modalWelcomeMessageNotEnabled, (prev) => ({
              ...prev,
              show: true
            }))
          } else {
            // GO TO STEP 2
            const step2Defaults = getStep2Defaults(selectedType, prevType)

            const StartType = WORKFLOWS_START_TYPE[selectedType]

            set(workflowDetails, (prev) => ({
              ...prev,
              ...step2Defaults,
              Type: selectedType,
              StartType
            }))

            set(wizardSteps, (prev) => ({
              ...prev,
              wizardStepsDone:
                nextStep > prev.wizardStepsDone
                  ? nextStep - 1
                  : prev.wizardStepsDone,
              wizardStep: nextStep
            }))

            if (Id && prevType !== selectedType) {
              set(pageState, (prev) => ({ ...prev, isDirty: true }))
            }
          }
        }
      } else {
        // GO TO STEP 2
        const step2Defaults = getStep2Defaults(selectedType, prevType)

        const StartType = WORKFLOWS_START_TYPE[selectedType]

        set(workflowDetails, (prev) => ({
          ...prev,
          ...step2Defaults,
          Type: selectedType,
          StartType
        }))

        set(wizardSteps, (prev) => ({
          ...prev,
          wizardStepsDone:
            nextStep > prev.wizardStepsDone
              ? nextStep - 1
              : prev.wizardStepsDone,
          wizardStep: nextStep
        }))

        if (Id && prevType !== selectedType) {
          set(pageState, (prev) => ({ ...prev, isDirty: true }))
        }
      }
    } catch (error) {
      logAppError({
        Component: 'Workflow',
        Method: 'step1ToStep2Callback',
        appError: error
      })
      set(pageState, (prev) => ({
        ...prev,
        error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
        severity: 'danger'
      }))
    }

    set(pageState, (prev) => ({
      ...prev,
      loading: false
    }))
  }

export const changeWizardStepCallback =
  ({ set, snapshot }) =>
  async (nextStep) => {
    set(wizardSteps, (prev) => ({
      ...prev,
      wizardStepsDone:
        nextStep > prev.wizardStepsDone ? nextStep - 1 : prev.wizardStepsDone,
      wizardStep: nextStep
    }))
  }

export const loadWizardStepDataCallback =
  ({ set, snapshot }) =>
  async () => {
    const { wizardStep } = await snapshot.getPromise(wizardSteps)
    const { Id, Type, Step } = await snapshot.getPromise(workflowDetails)

    set(pageState, (prev) => ({
      ...prev,
      loading: true
    }))

    set(wizardSteps, (prev) => ({
      ...prev,
      wizardStepsDone:
        wizardStep - 1 > prev.wizardStepsDone
          ? wizardStep - 1
          : prev.wizardStepsDone
    }))

    try {
      if (wizardStep === 2) {
        const { commonData } = await setupStep2(Type)
        set(workflowCommonDataState, (prev) => ({
          ...prev,
          ...commonData
        }))
      } else if (wizardStep === 3) {
        const tmp = await setupStep3(Id, Step)
        set(workflowCommonDataState, (prev) => ({ ...prev, ...tmp }))
      } else if (wizardStep === 5) {
        //  overview

        const emailIds = Step.filter(
          ({ IdMessage, MessageType }) =>
            MessageType === WORKFLOW_MESSAGE_TYPE_EMAIL && IdMessage > 0
        ).map(({ IdMessage }) => IdMessage)

        const smsIds = Step.filter(
          ({ IdMessage, MessageType }) =>
            MessageType === WORKFLOW_MESSAGE_TYPE_SMS && IdMessage > 0
        ).map(({ IdMessage }) => IdMessage)

        const [
          tmp,
          emailStats,
          smsStats,
          { groups },
          filters,
          conditionsDescription
        ] = await Promise.all([
          setupStep3(Id, Step),
          fetchEmailStats(emailIds),
          fetchSmsStats(smsIds),
          loadGroups(),
          fetchFilters(),
          getConditionsDescription(Id, true)
        ])

        set(workflowCommonDataState, (prev) => ({
          ...prev,
          ...tmp,
          groups,
          filters,
          conditionsDescription
        }))
        set(workflowEmailStatsState, (prev) => ({ ...prev, ...emailStats }))
        set(workflowSmsStatsState, (prev) => ({ ...prev, ...smsStats }))
      }
    } catch (error) {
      logAppError({
        Component: 'Workflow',
        Method: 'loadWizardStepDataCallback',
        appError: error
      })

      set(pageState, (prev) => ({
        ...prev,
        error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
        severity: 'danger'
      }))
    }
    set(pageState, (prev) => ({
      ...prev,
      loading: false
    }))
  }

export const addNewTagCallback =
  ({ set, snapshot }) =>
  async (pTagName) => {
    const { Tags: oldTags } = await snapshot.getPromise(step2State)
    const { tags: tagsList } = await snapshot.getPromise(
      workflowCommonDataState
    )

    try {
      const newTag = await addNewTag(pTagName)

      // update list with new tag
      set(workflowCommonDataState, (prev) => ({
        ...prev,
        tags: [newTag, ...tagsList]
      }))

      // add new tag to msg state
      set(step2State, (prev) => ({
        ...prev,
        Tags: [...oldTags, newTag.value]
      }))
    } catch (error) {
      logAppError({
        Component: 'OverviewMessage',
        Method: 'addNewTagCallback',
        appError: error
      })
      set(pageState, (prev) => ({
        ...prev,
        error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
        severity: 'danger'
      }))
    }
  }

export const saveStep2Callback =
  ({ set, snapshot }) =>
  async ({ onSuccess }) => {
    const { Id, Draft, ...data } = await snapshot.getPromise(workflowDetails)
    const step2 = await snapshot.getPromise(step2State)

    const useCustomFieldFilter = await snapshot.getPromise(
      fieldTypeExistingFilterState
    )

    const useCustomActivityFilter = await snapshot.getPromise(
      activiyTypeExistingFilterState
    )

    // const { wizardStep: currentStep } = await snapshot.getPromise(wizardSteps)

    const workflowData = {
      ...data,
      ...step2,
      IdFilter:
        (data.StartType === WORKFLOW_START_TYPE_SUBSCRIBER_FIELD &&
          useCustomFieldFilter === 'use-custom') ||
        (data.StartType === WORKFLOW_START_TYPE_ACTIVITY &&
          useCustomActivityFilter === 'use-custom')
          ? 0
          : step2.IdFilter,
      Draft,
      WizardStep: Id ? (Draft ? 3 : 5) : onSuccess ? 2 : 3 // create
    }

    /*
       Draft: Draft && !(currentStep === 4 && typeof onSuccess !== 'function'),
      WizardStep: Draft
        ? typeof onSuccess === 'function'
          ? currentStep
          : currentStep + 1 > WizardStep
          ? currentStep + 1
          : WizardStep
        : 5

    */

    try {
      if (Id > 0) {
        const updItem = await updateWorkflow(Id, workflowData)
        set(workflowDetails, (prev) => ({ ...prev, ...updItem }))
      } else {
        const newItem = await createWorkflow(workflowData)

        set(workflowDetails, (prev) => ({
          ...prev,
          ...newItem
        }))
      }

      set(pageState, (prev) => ({ ...prev, isDirty: false }))

      if (typeof onSuccess === 'function') {
        onSuccess()
      } else {
        set(wizardSteps, (prev) => ({
          ...prev,
          wizardStep: prev.wizardStep + 1,
          wizardStepsDone:
            prev.wizardStep > prev.wizardStepsDone
              ? prev.wizardStep
              : prev.wizardStepsDone
        }))
      }
    } catch (error) {
      logAppError({
        Component: 'AddEditWorkflow',
        Method: 'saveStep2Callback',
        appError: error
      })
      set(pageState, (prev) => ({
        ...prev,
        error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
        severity: 'danger'
      }))
    }

    set(pageState, (prev) => ({ ...prev, isDirty: false }))
  }

export const saveStep3Callback =
  ({ set, snapshot, reset }) =>
  async ({ onSuccess, forceSave }) => {
    const { Id, Draft, WizardStep, ...data } =
      await snapshot.getPromise(workflowDetails)

    const step3 = await snapshot.getPromise(step3State)

    const { wizardStep: currentStep } = await snapshot.getPromise(wizardSteps)

    const invalidFirstStep = await snapshot.getPromise(invalidFirstStepState)

    const { onSuccess: savedOnSuccess } = await snapshot.getPromise(
      modalInvalidFirstStepState
    )

    if (invalidFirstStep && !forceSave) {
      set(modalInvalidFirstStepState, (prev) => ({
        ...prev,
        show: true,
        onSuccess
      }))
    } else {
      const workflowData = {
        ...data,
        ...step3,
        Id,
        Draft,
        WizardStep: Draft
          ? typeof onSuccess === 'function'
            ? currentStep
            : currentStep + 1 > WizardStep
              ? currentStep + 1
              : WizardStep
          : 5
      }

      set(pageState, (prev) => ({
        ...prev,
        loading: true
      }))

      try {
        const updItem = await updateWorkflow(Id, workflowData)
        set(workflowDetails, (prev) => ({ ...prev, ...updItem }))

        reset(modalInvalidFirstStepState)

        set(pageState, (prev) => ({ ...prev, isDirty: false }))

        if (typeof onSuccess === 'function') {
          onSuccess()
        } else if (typeof savedOnSuccess === 'function') {
          savedOnSuccess()
        } else {
          set(wizardSteps, (prev) => ({
            ...prev,
            wizardStep: prev.wizardStep + 1,
            wizardStepsDone:
              prev.wizardStep > prev.wizardStepsDone
                ? prev.wizardStep
                : prev.wizardStepsDone
          }))
        }
      } catch (error) {
        logAppError({
          Component: 'Workflow',
          Method: 'saveStep3Callback',
          appError: error
        })
        set(pageState, (prev) => ({
          ...prev,
          error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
          severity: 'danger'
        }))
      }

      set(pageState, (prev) => ({
        ...prev,
        loading: false
      }))
    }
  }

export const saveStep4Callback =
  ({ set, snapshot, reset }) =>
  async ({ onSuccess }) => {
    const { Id, ...data } = await snapshot.getPromise(workflowDetails)

    const step4 = await snapshot.getPromise(step4State)

    const Draft = typeof onSuccess === 'function' // se exit lascio in draft
    const WizardStep = Draft ? 4 : 5

    const workflowData = {
      ...data,
      ...step4,
      Id,
      Draft,
      WizardStep
    }

    if (data.Type !== OPT_WORKFLOWS_LIST_TYPE_ABANDONED_CART__VALUE) {
      workflowData.FrequencySafeGuard = 0
    }

    set(pageState, (prev) => ({
      ...prev,
      loading: true
    }))

    try {
      const updItem = await updateWorkflow(Id, workflowData)
      set(workflowDetails, (prev) => ({ ...prev, ...updItem }))

      set(pageState, (prev) => ({ ...prev, isDirty: false }))

      if (typeof onSuccess === 'function') {
        onSuccess()
      } else {
        set(wizardSteps, (prev) => ({
          ...prev,
          wizardStep: prev.wizardStep + 1,
          wizardStepsDone:
            prev.wizardStep > prev.wizardStepsDone
              ? prev.wizardStep
              : prev.wizardStepsDone
        }))
      }
    } catch (error) {
      logAppError({
        Component: 'Workflow',
        Method: 'saveStep4Callback',
        appError: error
      })
      set(pageState, (prev) => ({
        ...prev,
        error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
        severity: 'danger'
      }))
    }

    set(pageState, (prev) => ({
      ...prev,
      loading: false
    }))
  }

export const addGroupCallback =
  ({ set }) =>
  async ({ name, notes, testGroup }) => {
    set(pageState, (prev) => ({
      ...prev,
      loading: true
    }))

    try {
      const newGroup = await addGroup(name, notes, testGroup)

      set(workflowCommonDataState, (prev) => ({
        ...prev,
        groups: [
          { value: newGroup.Id, label: `${newGroup.Id} - ${newGroup.Name}` },
          ...prev.groups
        ]
      }))

      set(workflowDetails, (prev) => ({ ...prev, IdGroup: newGroup.Id }))
    } catch (error) {
      logAppError({
        Component: 'OverviewMessage',
        Method: 'addGroupCallback',
        appError: error
      })
      set(pageState, (prev) => ({
        ...prev,
        error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
        severity: 'danger'
      }))
    }

    set(pageState, (prev) => ({
      ...prev,
      loading: false
    }))
  }

export const fetchMessageLinksCallback =
  ({ set, snapshot }) =>
  async () => {
    const { IdMessage, ActivityType, ClickedLink } =
      await snapshot.getPromise(step2State)
    const { allowedProtocols } = await snapshot.getPromise(
      workflowCommonDataState
    )

    try {
      if (
        [
          WORKFLOW_ACTIVITY_ACTION_CLICKED,
          WORKFLOW_ACTIVITY_ACTION_NOT_CLICKED
        ].includes(ActivityType) &&
        IdMessage > 0
      ) {
        set(pageState, (prev) => ({
          ...prev,
          loading: true
        }))
        const messageLinks = await fetchMessageLinks(
          IdMessage,
          allowedProtocols
        )

        //  aggiungo opzione se ClickedLink non fa parte dell'elenco
        const links =
          !ClickedLink || messageLinks.includes(ClickedLink)
            ? messageLinks
            : [ClickedLink, ...messageLinks]

        set(workflowCommonDataState, (prev) => ({
          ...prev,
          emailMessageLinks: links
        }))
      } else {
        set(workflowCommonDataState, (prev) => ({
          ...prev,
          emailMessageLinks: []
        }))
      }
    } catch (error) {
      logAppError({
        Component: 'OverviewMessage',
        Method: 'addGroupCallback',
        appError: error
      })
      set(pageState, (prev) => ({
        ...prev,
        error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
        severity: 'danger'
      }))
    }

    set(pageState, (prev) => ({
      ...prev,
      loading: false
    }))
  }

export const editCallback =
  ({ set, snapshot }) =>
  async (goToStep) => {
    const { Enabled } = await snapshot.getPromise(workflowDetails)

    if (Enabled) {
      set(modalEditDisableState, (prev) => ({ ...prev, show: true, goToStep }))
    } else {
      set(wizardSteps, (prev) => ({ ...prev, wizardStep: goToStep }))
    }
  }

export const disableAndEditConfirmCallback =
  ({ set, snapshot, reset }) =>
  async () => {
    set(pageState, (prev) => ({
      ...prev,
      loading: true
    }))

    const { Id } = await snapshot.getPromise(workflowDetails)
    const { goToStep } = await snapshot.getPromise(modalEditDisableState)

    try {
      await setEnableWorkflow(Id, false)

      set(workflowDetails, (prev) => ({ ...prev, Enabled: false }))
      set(wizardSteps, (prev) => ({ ...prev, wizardStep: goToStep }))
    } catch (error) {
      logAppError({
        Component: 'OverviewMessage',
        Method: 'addGroupCallback',
        appError: error
      })
      set(pageState, (prev) => ({
        ...prev,
        error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
        severity: 'danger'
      }))
    }

    reset(modalEditDisableState)

    set(pageState, (prev) => ({
      ...prev,
      loading: false
    }))
  }

export const getEmailSelectedMessageCallback =
  ({ snapshot, set }) =>
  async (itemEmail, stepItems, droppableId) => {
    const { Name: workflowName } = await snapshot.getPromise(workflowDetails)
    set(modalEmailMessagesState, (prev) => ({
      ...prev,
      selectedItem: itemEmail
    }))
    let ret = itemEmail
    const emailEntities = await fetchEmailEntitiesThatUseMessage(itemEmail.Id)
    // controllare l'uso in pagina negli items
    const isAlreadyUsedInPage = stepItems.find(
      (e) =>
        e.IdMessage === itemEmail.Id &&
        e.MessageType === WORKFLOW_MESSAGE_TYPE_EMAIL
    )

    let entityName = ''
    let entityType = ''
    let found = false
    if (emailEntities && emailEntities.length > 0) {
      emailEntities.forEach((element) => {
        if (
          !found &&
          (element.Type === 'AutomationWorkflow' ||
            element.Type === 'Trigger') &&
          element.Id !== itemEmail.Id
        ) {
          entityName = element.Name
          entityType = element.Type
          found = true
        }
      })
    }

    if (found || isAlreadyUsedInPage) {
      // usato
      set(modalUsedMessageState, (prev) => ({
        ...prev,
        show: true,
        entityName: isAlreadyUsedInPage ? workflowName : entityName,
        entityType: isAlreadyUsedInPage ? 'AutomationWorkflow' : entityType,
        type: WORKFLOW_MESSAGE_TYPE_EMAIL,
        droppableId // usato per la riapertura della modale
      }))
      ret = undefined
    }

    set(modalEmailMessagesState, (prev) => ({
      ...prev,
      show: false
    }))
    return ret
  }

export const getSmsSelectedMessageCallback =
  ({ snapshot, set }) =>
  async (itemSms, stepItems, droppableId) => {
    const { Name: workflowName } = await snapshot.getPromise(workflowDetails)
    set(modalSmsMessagesState, (prev) => ({
      ...prev,
      selectedItem: itemSms
    }))
    let ret = itemSms
    const smsEntities = await fetchSmsEntitiesThatUseMessage(itemSms.Id)
    // controllare l'uso in pagina negli items
    const isAlreadyUsedInPage = stepItems.find(
      (e) =>
        e.IdMessage === itemSms.Id &&
        e.MessageType === WORKFLOW_MESSAGE_TYPE_SMS
    )
    // esiste Entities[i].Type=='Trigger' || esiste Entities[i].Type=='AutomationWorkflow') && Entities[i].Id != ID
    let entityName = ''
    let entityType = ''
    let found = false
    if (smsEntities && smsEntities.length > 0) {
      smsEntities.forEach((element) => {
        if (
          !found &&
          (element.Type === 'AutomationWorkflow' ||
            element.Type === 'Trigger') &&
          element.Id !== itemSms.Id
        ) {
          entityName = element.Name
          entityType = element.Type
          found = true
        }
      })
    }

    if (found || isAlreadyUsedInPage) {
      // usato
      set(modalUsedMessageState, (prev) => ({
        ...prev,
        show: true,
        entityName: isAlreadyUsedInPage ? workflowName : entityName,
        entityType: isAlreadyUsedInPage ? 'AutomationWorkflow' : entityType,
        type: WORKFLOW_MESSAGE_TYPE_SMS,
        droppableId // usato per la riapertura della modale
      }))
      ret = undefined
    }

    set(modalSmsMessagesState, (prev) => ({
      ...prev,
      show: false
    }))
    return ret
  }

export const copyMessageCallback =
  ({ snapshot, set }) =>
  async () => {
    const { selectedItem } = await snapshot.getPromise(modalEmailMessagesState)
    const { entityName, entityType } = await snapshot.getPromise(
      modalUsedMessageState
    )
    const { TimeZoneCode: tzcode } = storeGet(STORE_KEY_LIST)
    // Se Entities[i].Type=='Trigger' => CopyReason = Load.EmailMessage.CopyTriggerMessageNote
    // Se Entities[i].Type=='AutomationWorkflow' => CopyReason = Load.EmailMessage.CopyWorkflowMessageNote
    // CopyReason => replace('{0}', idMessage).replace(('{1}', Now [yyyy-MM-dd HH:mm:ss UTC] ).replace(('{2}', Entities[i].Name )
    let str = ''
    switch (entityType.toLowerCase()) {
      case 'trigger':
        str = getIntl().formatMessage({
          id: 'Load.EmailMessage.CopyTriggerMessageNote'
        })
        break
      case 'automationworkflow':
        str = getIntl().formatMessage({
          id: 'Load.EmailMessage.CopyWorkflowMessageNote'
        })
        break
      default:
        break
    }
    const notes = str
      .replace('{0}', selectedItem.Id)
      .replace(
        '{1}',
        fromUTCtoZoneFormatted(new Date(), tzcode, 'YYYY-MM-DD HH:mm:ss')
      )
      .replace('{2}', entityName)

    const ret = await copyMessage(selectedItem.Id, notes)
    return ret
  }

export const copyMessageSmsCallback =
  ({ snapshot, set }) =>
  async () => {
    const { selectedItem } = await snapshot.getPromise(modalSmsMessagesState)
    const { entityName, entityType } = await snapshot.getPromise(
      modalUsedMessageState
    )
    const { TimeZoneCode: tzcode } = storeGet(STORE_KEY_LIST)
    let str = ''
    switch (entityType.toLowerCase()) {
      case 'Trigger'.toLowerCase():
        str = getIntl().formatMessage({
          id: 'Load.EmailMessage.CopyTriggerMessageNote'
        })
        break
      case 'AutomationWorkflow'.toLowerCase():
        str = getIntl().formatMessage({
          id: 'Load.EmailMessage.CopyWorkflowMessageNote'
        })
        break
      default:
        break
    }
    const notes = str
      .replace('{0}', selectedItem.Id)
      .replace(
        '{1}',
        fromUTCtoZoneFormatted(new Date(), tzcode, 'YYYY-MM-DD HH:mm:ss')
      )
      .replace('{2}', entityName)

    const ret = await copySmsMessage(selectedItem.Id, notes)
    return ret
  }

export const initSegmentationCriteriaCallback =
  ({ set, snapshot }) =>
  async () => {
    set(segmentationCriteriaState, (prev) => ({ ...prev, loading: true }))

    try {
      const [groups, filters] = await Promise.all([
        getGroupsDetails(),
        getFilters()
      ])

      set(segmentationCriteriaState, (prev) => ({
        ...prev,
        groups,
        filters
      }))
      set(workflowDetails, (prev) => ({
        ...prev
      }))
    } catch (error) {
      logAppError({
        appError: error,
        Component: 'SendingWizard',
        Method: 'searchCriteriaCallback',
        Message: 'Errore in ottieni criteri di ricerca'
      })
      set(pageState, (prev) => ({
        ...prev,
        error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
        severity: 'danger'
      }))
    }

    set(segmentationCriteriaState, (prev) => ({
      ...prev,
      loading: false,
      loaded: true
    }))
  }

/**
 * on step exit => reset copies to wf state
 */
export const resetStepCallback =
  ({ set, snapshot }) =>
  async () => {
    const {
      Name,
      Notes,
      FromName,
      FromEmail,
      ReplyTo,
      FromSms,
      Tags,
      Action,
      ActivityType,
      ActivityValue,
      ActivityValueType,
      IdFilter,
      IdsFilter,
      RecipientLimit,
      LastSendingPeriod,
      EndEmailStatus,
      ClickedLink,
      IdMessage,
      IdGroup,
      IdField,
      FieldType,
      FieldValue,
      IdFieldFilter,
      Step,
      EndFromGroup,
      EndToGroup,
      Group,
      Filter,
      FiltersRule,
      StartHour,
      BlackoutStartHour,
      BlackoutEndHour,
      TimeZoneCode,
      StartDate,
      EndDate,
      FrequencyBlackoutDate,
      NotInWeekends,
      FrequencySafeGuard
    } = await snapshot.getPromise(workflowDetails)

    set(step2State, {
      //  left
      Name,
      Notes,
      FromName,
      FromEmail,
      ReplyTo,
      FromSms,
      Tags,

      //  right
      Action,
      ActivityType,
      ActivityValue,
      ActivityValueType,
      IdFilter,
      IdsFilter,
      RecipientLimit,
      LastSendingPeriod,
      EndEmailStatus,
      ClickedLink,
      IdMessage,
      IdGroup,
      IdField,
      FieldType,
      FieldValue,
      IdFieldFilter
    })

    set(step3State, {
      Step,
      EndFromGroup,
      EndToGroup
    })

    set(step4State, {
      Group,
      Filter,
      FiltersRule,
      StartHour,
      BlackoutStartHour,
      BlackoutEndHour,
      TimeZoneCode,
      StartDate,
      EndDate,
      FrequencyBlackoutDate,
      NotInWeekends,
      FrequencySafeGuard
    })
  }

export const resetPageCallback =
  ({ set, reset }) =>
  async () => {
    set(workflowDetails, emptyWorkflow)
    reset(pageState)
    reset(modalActivateSimulateState)
  }
